/**
 * Befund-Kennzeichen an Karten (PV-052, A10): Ausrufezeichen in der Farbe der höchsten Stufe,
 * Klick öffnet ein Popover NUR mit den Befunden und ihren Aktionen.
 *
 * Das Popover selbst (Portal, Nachmessen, Schließen) ist `components/v2/popover.tsx` (PV-037).
 * Der Marker ist nicht ziehbar und stoppt pointerdown/click/keydown, damit weder ein Drag
 * der Karte noch das Aufklappen der Worklist-Zeile startet.
 */
import type { BefundUebersichtEintrag } from '@projektverwaltung/shared';
import { useQuery } from '@tanstack/react-query';
import { type RefObject, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { useFeatureFlag } from '../../../lib/features.js';
import { Popover } from '../popover.js';
import { BEFUNDE_QUERY_KEY, STUFE_FARBE, useBefundLocale } from './befund-basis.js';
import { BefundZeile } from './befund-zeile.js';

/** Eine Abfrage je Seite (§9); bei Flag aus leer und ohne Abfrage. */
export function useBefundUebersicht(): ReadonlyMap<string, BefundUebersichtEintrag> {
  const an = useFeatureFlag('befunde');
  const locale = useBefundLocale();
  const query = useQuery({
    queryKey: [...BEFUNDE_QUERY_KEY, 'uebersicht', locale],
    queryFn: () => api.getBefundUebersicht(locale),
    enabled: an,
    staleTime: 60_000,
  });
  return useMemo(
    () => new Map(an ? Object.entries(query.data?.items ?? {}) : []),
    [an, query.data],
  );
}

export function BefundMarker({
  projectId,
  eintrag,
}: { projectId: string; eintrag: BefundUebersichtEintrag }) {
  const { t } = useTranslation();
  const [offen, setOffen] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  // Mouseover zeigt sofort, was los ist (Feedback 1, F6): „Handeln: Mission Briefing fehlt“.
  // Jeder Befund nennt seine EIGENE Stufe (Feedback 1, L1) — Stufen unterscheiden sich zwischen
  // den Befunden eines Projekts, die höchste vor allen zu nennen wäre für die anderen falsch.
  const tooltip = eintrag.befunde
    .map((b) =>
      t('v2.befund.marker_tooltip', '{{stufe}}: {{titel}}', {
        stufe: t(`v2.befund.stufe_${b.stufe}`, b.stufe),
        titel: b.titel,
      }),
    )
    .join('\n');
  return (
    <>
      <button
        ref={knopf}
        type="button"
        draggable={false}
        aria-expanded={offen}
        title={tooltip}
        aria-label={t('v2.befund.marker_label', 'Befunde anzeigen ({{count}})', {
          count: eintrag.anzahl,
        })}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onDragStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOffen((v) => !v);
        }}
        className="inline-flex items-center justify-center rounded-full text-xs font-bold"
        style={{
          width: 18,
          height: 18,
          color: 'var(--surface)',
          background: STUFE_FARBE[eintrag.hoechste_stufe],
          cursor: 'pointer',
        }}
      >
        !
      </button>
      {offen && (
        <BefundPopover projectId={projectId} anker={knopf} onClose={() => setOffen(false)} />
      )}
    </>
  );
}

export function BefundPopover({
  projectId,
  anker,
  onClose,
}: {
  projectId: string;
  anker: RefObject<HTMLElement>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const locale = useBefundLocale();
  const query = useQuery({
    queryKey: [...BEFUNDE_QUERY_KEY, 'projekt', projectId, locale],
    queryFn: () => api.listBefundeProjekt(projectId, locale),
  });

  const items = query.data?.items ?? [];
  return (
    <Popover
      anker={anker}
      onClose={onClose}
      label={t('v2.befund.popover_label', 'Befunde zum Projekt')}
    >
      {query.isError ? (
        <p className="py-3 text-sm" style={{ color: 'var(--danger)' }}>
          {t(
            'v2.befund.laden_fehler',
            'Befunde konnten nicht geladen werden. Bitte Seite neu laden.',
          )}
        </p>
      ) : query.isLoading ? (
        <p className="py-3 text-sm" style={{ color: 'var(--ink-3)' }}>
          {t('v2.befund.laedt', 'Lädt…')}
        </p>
      ) : items.length === 0 ? (
        <p className="py-3 text-sm" style={{ color: 'var(--ink-3)' }}>
          {t('v2.befund.popover_leer', 'Keine offenen Befunde.')}
        </p>
      ) : (
        <ul>
          {items.map((b) => (
            <BefundZeile key={b.befund_id} befund={b} />
          ))}
        </ul>
      )}
    </Popover>
  );
}
